import { CircleSlash, type LucideIcon } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import type { BadgeVariant } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';

export interface WorkflowStepExecutionSummary {
  name: string;
  /**
   * `skipped`: the engine did not run the step because its `condition` was
   * not met (e.g. the claim-motif steps of the close-task workflow when no
   * motif was chosen) — shown muted, never with an error block.
   */
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'skipped';
  errorMessage?: string | null;
}

export interface WorkflowStepTrackerProps {
  steps: WorkflowStepExecutionSummary[];
}

type WorkflowStepStatus = WorkflowStepExecutionSummary['status'];

interface WorkflowStepStatusDisplay {
  /** Badge variant of the status. */
  variant: BadgeVariant;
  /** Status label shown in the badge. */
  label: string;
  /** Optional muted icon shown in the badge, before the label. */
  icon?: LucideIcon;
  /** The step did not run: its name is shown muted. */
  muted?: boolean;
}

/**
 * Per-status display config (badge variant, label, icon) — the texts live
 * here, never in the JSX.
 */
const WORKFLOW_STEP_STATUS_DISPLAY: Record<WorkflowStepStatus, WorkflowStepStatusDisplay> = {
  pending: { variant: 'default', label: 'En attente' },
  running: { variant: 'info', label: 'En cours' },
  completed: { variant: 'success', label: 'Terminé' },
  failed: { variant: 'error', label: 'Échoué' },
  cancelled: { variant: 'default', label: 'Annulé' },
  skipped: { variant: 'default', label: 'Ignorée', icon: CircleSlash, muted: true },
};

export function WorkflowStepTracker({ steps }: WorkflowStepTrackerProps) {
  return (
    <VStack gap={2}>
      {steps.map((step) => {
        const display = WORKFLOW_STEP_STATUS_DISPLAY[step.status];
        return (
          <HStack key={step.name} gap={2} align="start">
            <Badge variant={display.variant}>
              {display.icon ? (
                <HStack as="span" inline gap={1} align="center">
                  <Icon icon={display.icon} size="xs" color="muted" />
                  {display.label}
                </HStack>
              ) : (
                display.label
              )}
            </Badge>
            {step.status === 'failed' && step.errorMessage ? (
              <VStack>
                <Text size="sm" weight="medium">
                  {step.name}
                </Text>
                <Text size="sm" color="muted">
                  {step.errorMessage}
                </Text>
              </VStack>
            ) : (
              <Text size="sm" weight="medium" color={display.muted ? 'muted' : 'default'}>
                {step.name}
              </Text>
            )}
          </HStack>
        );
      })}
    </VStack>
  );
}
